export type HeroHighlight = {
  icon?: string
  title?: string
}

export type HeroCta = {
  label?: string
  icon?: string
  icon_position?: 'left' | 'right'
  appearance?: 'button' | 'link'
  type?: 'internal' | 'external' | 'scroll'
  post_id?: number
  url?: string
  external_url?: string
  scroll_target_uuid?: string
}

export type HeroVideo = {
  source?: 'local' | 'external'
  file_id?: number
  file_url?: string
  url?: string
  embed_url?: string
  provider_id?: string
}

export type HeroMedia = {
  type?: 'none' | 'image' | 'video'
  has_media?: boolean
  image_id?: number
  image_url?: string
  image_alt?: string
  video?: HeroVideo
}

export type RawHero = {
  icon?: unknown
  supertitle?: unknown
  title?: unknown
  intro_content?: unknown
  intro_content_html?: unknown
  disclaimer?: unknown
  disclaimer_html?: unknown
  highlights?: HeroHighlight[] | null
  ctas?: HeroCta[] | null
  media?: HeroMedia | null
  layout?: unknown
  height?: unknown
  size?: unknown
  width?: unknown

  settings?: {
    display_mode?: string
    hide_hero?: boolean
    hide_title?: boolean
  }
}

export type Hero = {
  icon?: string
  supertitle?: string
  title?: string
  introContent?: string
  introDisclaimer?: string
  highlights: HeroHighlight[]
  ctas: HeroCta[]
  media?: HeroMedia
  layout?: string
  height?: string
  size?: string
  width?: string

  settings?: RawHero['settings']
}

function cleanString(value: unknown) {
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  return trimmed || undefined
}

function normalizeMediaType(value: unknown): HeroMedia['type'] {
  return value === 'none' || value === 'image' || value === 'video'
    ? value
    : undefined
}

function normalizeVideoSource(value: unknown): HeroVideo['source'] {
  return value === 'local' || value === 'external'
    ? value
    : undefined
}

function normalizeHighlights(input: RawHero['highlights']) {
  if (!Array.isArray(input)) return []

  return input
    .map((item) => ({
      icon: cleanString(item?.icon),
      title: cleanString(item?.title),
    }))
    .filter((item) => item.icon || item.title)
}

function normalizeCtas(input: RawHero['ctas']) {
  if (!Array.isArray(input)) return []

  return input
    .map((item) => ({
      label: cleanString(item?.label),
      icon: cleanString(item?.icon),
      icon_position:
        item?.icon_position === 'left' || item?.icon_position === 'right'
          ? item.icon_position
          : undefined,
      appearance:
        item?.appearance === 'button' || item?.appearance === 'link'
          ? item.appearance
          : undefined,
      type:
        item?.type === 'internal' || item?.type === 'external' || item?.type === 'scroll'
          ? item.type
          : undefined,
      post_id: typeof item?.post_id === 'number' ? item.post_id : undefined,
      url: cleanString(item?.url),
      external_url: cleanString(item?.external_url),
      scroll_target_uuid: cleanString(item?.scroll_target_uuid),
    }))
    .filter((item) => item.label && item.type)
}

function normalizeMedia(input: RawHero['media']) {
  if (!input || typeof input !== 'object') return undefined

  const media: HeroMedia = {
    type: normalizeMediaType(input.type),
    has_media: Boolean(input.has_media),
    image_id: typeof input.image_id === 'number' ? input.image_id : undefined,
    image_url: cleanString(input.image_url),
    image_alt: cleanString(input.image_alt),
    video: input.video
      ? {
          source: normalizeVideoSource(input.video.source),
          file_id: typeof input.video.file_id === 'number' ? input.video.file_id : undefined,
          file_url: cleanString(input.video.file_url),
          url: cleanString(input.video.url),
          embed_url: cleanString(input.video.embed_url),
          provider_id: cleanString(input.video.provider_id),
        }
      : undefined,
  }

  const hasUsefulMedia =
    media.has_media ||
    media.type === 'image' ||
    media.type === 'video' ||
    !!media.image_id ||
    !!media.image_url ||
    !!media.video?.file_url ||
    !!media.video?.url ||
    !!media.video?.embed_url

  return hasUsefulMedia ? media : undefined
}

export function normalizeHero(input: RawHero | null | undefined) {
  if (!input || typeof input !== 'object') return undefined

  const supertitle = cleanString(input.supertitle)
  const title = cleanString(input.title)
  const introContent =
    cleanString(input.intro_content_html) ??
    cleanString(input.intro_content)
  const introDisclaimer =
    cleanString(input.disclaimer_html) ??
    cleanString(input.disclaimer)

  const normalized: Hero = {
    icon: cleanString(input.icon),
    supertitle,
    title,
    introContent,
    introDisclaimer,
    highlights: normalizeHighlights(input.highlights),
    ctas: normalizeCtas(input.ctas),
    media: normalizeMedia(input.media),
    layout: cleanString(input.layout),
    height: cleanString(input.height),
    size: cleanString(input.size),
    width: cleanString(input.width),
    settings: input.settings,
  }

  const hasContent =
    !!normalized.icon ||
    !!normalized.supertitle ||
    !!normalized.title ||
    !!normalized.introContent ||
    !!normalized.introDisclaimer ||
    normalized.highlights.length > 0 ||
    normalized.ctas.length > 0 ||
    !!normalized.media ||
    !!normalized.layout ||
    !!normalized.height ||
    !!normalized.size ||
    !!normalized.width ||
    !!normalized.settings

  return hasContent ? normalized : undefined
}
